import { fetchDetails } from '@/modules/check-in/service/fetchDetails'
import { fetchTags } from '@/modules/check-in/service/fetchTags'
import { fetchGiftCatalog } from '@/modules/check-in/service/fetchGiftCatalog'

export const CHECK_IN_MODULE = 'CHECK_IN'

// Modo da ESTAÇÃO — por dispositivo (localStorage), não global: o cliente roda
// as 3 coisas (check-in / lead / brinde) em paralelo no mesmo evento.
export const STATION_MODES = ['checkin', 'lead', 'brinde']
const MODE_KEY = 'checkin_station_mode'
const BRINDE_TAG_KEY = 'checkin_brinde_tag' // legado (tag única)
const BRINDE_TAGS_KEY = 'checkin_brinde_tags' // brindes da estação (várias tags) [{id,name}]

const loadMode = () => {
  try {
    const m = localStorage.getItem(MODE_KEY)
    return STATION_MODES.includes(m) ? m : 'checkin'
  } catch (e) { return 'checkin' }
}
// Um brinde = uma tag. A estação pode operar VÁRIOS brindes (várias tags do leitor).
const loadBrindeTags = () => {
  try {
    const raw = localStorage.getItem(BRINDE_TAGS_KEY)
    if (raw) return JSON.parse(raw) || []
    // Migração do formato antigo (tag única) → array.
    const old = localStorage.getItem(BRINDE_TAG_KEY)
    return old ? [JSON.parse(old)] : []
  } catch (e) { return [] }
}

export default {
  state: {
    list: {},
    tags: {
      list: [],
      alreadyLoaded: false
    },
    // Catálogo de brindes (tags classificadas como `gift`). Fonte do checklist
    // no modo Brinde — independe do que o operador escolheu no leitor.
    giftCatalog: {
      list: [],
      alreadyLoaded: false
    },
    // Filtro de brinde da LISTA (modo brinde): null = todos os brindes (visão
    // agregada); um id = escopa a lista/abas/contadores àquele brinde.
    brindeFilter: null,
    // Brinde "lido agora" pelo scanner — transitório: setado quando o leitor
    // abre a ficha, limpo ao fechar. Só ele acende o selo "Lido agora".
    scannedGiftTagId: null,
    showDetailId: null,
    // Modo da estação e os brindes (tags) da estação, persistidos por dispositivo.
    stationMode: loadMode(),
    brindeTags: loadBrindeTags(),
    // Contadores das abas (Todos / Aguardando / concluídos) do modo atual.
    counts: { total: null, aguardando: null, done: null },
    // Sinal de coleta de lead concluída (feita no sidebar): a lista observa o
    // tick para marcar o item e atualizar os contadores em tempo real.
    leadCollectedSignal: { id: null, tick: 0 }
  },
  actions: {
    async fetchDetail ({ state, commit, dispatch }, { id }) {
      await dispatch('loadTagsList')
      await dispatch('loadGiftCatalog')
      const response = await fetchDetails(id)

      // Tags atribuídas ao participante: vêm sempre completas (sem filtro de
      // escopo do operador) — assim o operador vê o contexto inteiro, mesmo
      // que ele só possa setar/remover as tags do allowed_tag_ids dele.
      // Cada tag é marcada como editable=true só se está no escopo do operador.
      const allowedIds = new Set((state.tags.list || []).map(t => t.id))
      const assignedTags = (response.tags || []).map(tag => ({
        ...tag,
        isChecked: true,
        isEditable: allowedIds.has(tag.id)
      }))

      // Tags do escopo que o participante NÃO tem (pra admin poder adicionar
      // via toggle). Operadores não conseguem clicar nessas (gate é por permissão).
      const assignedIds = new Set(assignedTags.map(t => t.id))
      const addableTags = (state.tags.list || [])
        .filter(t => !assignedIds.has(t.id))
        .map(t => ({ ...t, isChecked: false, isEditable: true }))

      commit('setItem', {
        id: response.id,
        isStatusCheckIn: response.isStatusCheckIn,
        isStatusCheckOut: response.isStatusCheckOut,
        isStatusUnseen: !response.isStatusCheckIn && !response.isStatusCheckOut,
        name: response.name,
        email: response.email,
        badge_name: response.badge_name,
        school: response.school,
        codId: response.codId,
        grupo: response.grupo,
        tags: [...assignedTags, ...addableTags],
        historico: response.historico,
        activitiesList: response.activitiesList,
        commentsList: response.commentsList
      })
    },
    async loadTagsList ({ state, commit }) {
      if (state.tags.alreadyLoaded) {
        return
      }
      const tags = await fetchTags()
      commit('updateTags', { tags })
    },
    async loadGiftCatalog ({ state, commit }) {
      if (state.giftCatalog.alreadyLoaded) {
        return
      }
      try {
        const gifts = await fetchGiftCatalog()
        commit('setGiftCatalog', { gifts })
      } catch (e) {
        commit('setGiftCatalog', { gifts: [] })
      }
    }
  },
  getters: {
    detailById: state => id => {
      return state.list[id] ?? undefined
    },
    stationMode: state => state.stationMode,
    brindeTags: state => state.brindeTags,
    // Compat: telas que ainda operam com uma tag única usam a primeira.
    brindeTag: state => (state.brindeTags && state.brindeTags.length ? state.brindeTags[0] : null),
    // Catálogo de brindes (tags `gift`) — fonte do checklist do modo Brinde.
    giftCatalog: state => state.giftCatalog.list,
    brindeFilter: state => state.brindeFilter,
    scannedGiftTagId: state => state.scannedGiftTagId,
    checkinCounts: state => state.counts,
    leadCollectedSignal: state => state.leadCollectedSignal
  },
  mutations: {
    setItem (state, {
      id,
      isStatusCheckIn,
      isStatusCheckOut,
      isStatusUnseen,
      name,
      email,
      // eslint-disable-next-line camelcase
      badge_name,
      school,
      codId,
      grupo,
      tags,
      historico,
      activitiesList,
      commentsList
    }) {
      const item = {
        id,
        isStatusCheckIn,
        isStatusCheckOut,
        isStatusUnseen,
        name,
        email,
        badge_name,
        school,
        codId,
        grupo,
        tags,
        historico,
        activitiesList,
        commentsList
      }
      state.list = { ...state.list, [id]: item }
    },
    updateTags (state, { tags }) {
      state.tags.alreadyLoaded = true
      // Substitui (não faz push): chamadas concorrentes de loadTagsList não
      // duplicam mais as tags no seletor.
      state.tags.list = tags || []
    },
    setGiftCatalog (state, { gifts }) {
      state.giftCatalog.alreadyLoaded = true
      state.giftCatalog.list = gifts || []
    },
    setBrindeFilter (state, id) {
      state.brindeFilter = id != null ? id : null
    },
    updateItemStatus (state, { id, isStatusCheckIn, isStatusCheckOut, isStatusUnseen, userLoggedInName = '', details = '' }) {
      const item = state.list[id]
      if (!item) {
        return
      }
      item.isStatusCheckIn = isStatusCheckIn
      item.isStatusCheckOut = isStatusCheckOut
      item.isStatusUnseen = isStatusUnseen
      if (!item.isStatusUnseen) {
        item.activitiesList.unshift({
          at: new Date(),
          by: userLoggedInName,
          isManual: false,
          isStatusCheckIn: isStatusCheckIn,
          details
        })
      }
    },
    setShowDetailId (state, id) {
      state.showDetailId = id
      // Fechar o sidebar limpa o "Lido agora" — ele só vale pra sessão de leitura
      // que abriu a ficha via scanner (não pode ficar marcado o tempo todo).
      if (id == null) {
        state.scannedGiftTagId = null
      }
    },
    setScannedGiftTagId (state, id) {
      state.scannedGiftTagId = id != null ? Number(id) : null
    },
    setStationMode (state, mode) {
      const valid = STATION_MODES.includes(mode) ? mode : 'checkin'
      state.stationMode = valid
      try { localStorage.setItem(MODE_KEY, valid) } catch (e) { /* localStorage off */ }
    },
    setBrindeTags (state, tags) {
      state.brindeTags = Array.isArray(tags) ? tags : []
      try {
        if (state.brindeTags.length) localStorage.setItem(BRINDE_TAGS_KEY, JSON.stringify(state.brindeTags))
        else localStorage.removeItem(BRINDE_TAGS_KEY)
        localStorage.removeItem(BRINDE_TAG_KEY) // limpa o formato antigo
      } catch (e) { /* localStorage off */ }
    },
    // Compat: alguns pontos ainda setam uma tag única.
    setBrindeTag (state, tag) {
      const tags = tag ? [tag] : []
      state.brindeTags = tags
      try {
        if (tags.length) localStorage.setItem(BRINDE_TAGS_KEY, JSON.stringify(tags))
        else localStorage.removeItem(BRINDE_TAGS_KEY)
        localStorage.removeItem(BRINDE_TAG_KEY)
      } catch (e) { /* localStorage off */ }
    },
    setCheckinCounts (state, counts) {
      state.counts = counts || { total: null, aguardando: null, done: null }
    },
    markLeadCollected (state, id) {
      state.leadCollectedSignal = { id, tick: state.leadCollectedSignal.tick + 1 }
    }
  },
  namespaced: true
}
